import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Icon } from "@vds/icons";
import "../AddressModal.css";
import { CommonValidateAddress } from "../CommonValidateAddress";

//  Validation regexes
const PHONE_REGEX = /^\+?[1-9]\d{9,14}$/; // 10-15 digits, optional leading +
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // standard email format
const INITIAL_ADDRESS_STATE = {
  verified: false,
  addressByPass: null,
  locationFound: null,
  timezone: "",
  latitude: "",
  longitude: "",
  locusSuggestedAddress: "",
  historicalAddress: { value: "", error: false, errorText: "", success: false },
  address1: { value: "", error: false, errorText: "Enter valid street address", success: false },
  address2: { value: "", error: false, errorText: "Enter address 2", success: false },
  city: { value: "", error: false, errorText: "Enter city", success: false },
  country: { value: null, options: [], error: false, errorText: "Please select country", success: false },
  state: { value: null, options: [], allStateOptions: null, error: false, errorText: "Please select state", success: false },
  zip: { value: "", error: false, errorText: "Enter zip code", success: false },
};

const AddressModal = ({ isOpen, newAddressContact, onContactChange, onSave, onCancel, label, typeofModal }) => {
  const [isLoading, setLoader] = useState(false);
  const [address, setAddress] = useState(INITIAL_ADDRESS_STATE);
  const [errors, setErrors] = useState({ phone: "", email: "" });
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  useEffect(() => {
    const isPhoneValid = !newAddressContact.phone || PHONE_REGEX.test((newAddressContact.phone || "").replace(/[\s\-().]/g, ""));
    const isEmailValid = !newAddressContact.email || EMAIL_REGEX.test(newAddressContact.email || "");

    const disabled =
      !newAddressContact.contactName?.trim() ||
      !newAddressContact.phone?.trim() ||
      !newAddressContact.email?.trim() ||
      !isPhoneValid ||
      !isEmailValid ||
      !address?.verified; // ✅ Now this will update when address.verified changes

    setIsSaveDisabled(disabled);
  }, [
    newAddressContact.contactName,
    newAddressContact.phone,
    newAddressContact.email,
    address?.verified, // ✅ Add address.verified as dependency
  ]);

  //  Validate on blur
  const handleBlur = (field) => {
    if (field === "phone") {
      const isValid = PHONE_REGEX.test((newAddressContact.phone || "").replace(/[\s\-().]/g, ""));
      setErrors((prev) => ({
        ...prev,
        phone: newAddressContact.phone && !isValid ? "Please enter a valid phone number (e.g. 1234567890)" : "",
      }));
    }
    if (field === "email") {
      const isValid = EMAIL_REGEX.test(newAddressContact.email || "");
      setErrors((prev) => ({
        ...prev,
        email: newAddressContact.email && !isValid ? "Please enter a valid email address" : "",
      }));
    }
  };
  // ✅ Early return AFTER all hooks
  if (!isOpen) return null;

  const handleSave = () => {
    let contactObj = {
      contactName: newAddressContact.contactName?.trim(),
      phone: newAddressContact.phone?.trim(),
      email: newAddressContact.email?.trim(),
    };
    onSave(address, contactObj);
  };
  const handleCancel = () => {
    setAddress(INITIAL_ADDRESS_STATE);
    onCancel();
  };
  return (
    <div className="install-modal-overlay">
      <div className="install-modal-container">
        {/* Close button */}
        <button className="install-modal-close-btn" onClick={handleCancel} aria-label="Close modal">
          <Icon name="close" size="small" color="#000" />
        </button>

        {/* Title */}
        <h3 className="install-modal-title">{label}</h3>
        {typeofModal === "shipping" && (
          <CommonValidateAddress
            address={address}
            setAddress={setAddress}
            showHistoricalAddressDropdown={true}
            setLoader={setLoader}
            variant="vehicle"
            showTitle={false}
            showTypedAddressOption={false}
            defaultCountryOnNew="USA"
            trackLocusFields={true}
            styles={{ containerWidth: "100%", radioGroupWidth: "100%" }}
            showShippingAddressLabel={false}
            bulkActionShippingPopup={true}
            modal={true}
          />
        )}

        {/* Contact Name & Phone - side by side */}
        <div className="install-modal-row">
          <div className="install-modal-field">
            <label className="install-modal-label">Contact name</label>
            <input
              type="text"
              className="install-modal-input"
              value={newAddressContact.contactName}
              onChange={(e) => onContactChange("contactName", e.target.value)}
            />
          </div>
          <div className="install-modal-field">
            <label className="install-modal-label">Phone</label>
            <input
              type="tel"
              className={`install-modal-input${errors.phone ? " install-modal-input-error" : ""}`}
              value={newAddressContact.phone}
              onChange={(e) => onContactChange("phone", e.target.value)}
              onBlur={() => handleBlur("phone")}
            />
            {errors.phone && <span className="install-modal-error-msg">{errors.phone}</span>}
          </div>
        </div>

        {/* Email - full width */}
        <div className="install-modal-field-full">
          <label className="install-modal-label">Email</label>
          <input
            type="email"
            className={`install-modal-input${errors.email ? " install-modal-input-error" : ""}`}
            value={newAddressContact.email}
            onChange={(e) => onContactChange("email", e.target.value)}
            onBlur={() => handleBlur("email")}
          />
          {errors.email && <span className="install-modal-error-msg">{errors.email}</span>}
        </div>

        {/* Cancel & Save Buttons */}
        <div className="install-modal-actions">
          <button className="install-modal-cancel-btn" onClick={handleCancel}>
            Cancel
          </button>
          <button className="install-modal-save-btn" onClick={handleSave} disabled={isSaveDisabled}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

AddressModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  newAddressContact: PropTypes.shape({
    contactName: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
  }).isRequired,
  onContactChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  label: PropTypes.string,
  typeofModal: PropTypes.string,
};

export default AddressModal;
